import React, { useMemo } from 'react';
import TimerOverlay from 'modules/layout/components/TimerManager/TimerOverlay';
import GlobalTooltip from 'components/GlobalTooltip';
import useActivityTypes from 'hooks/useActivityTypes';
import useTimerManager from 'hooks/useTimerManager';
import { useToast } from 'context/ToastContext';

const billingCodeOptionTemplate = (option) => {
  return (
    <div style={{ padding: '8px 0' }}>
      <div style={{ fontWeight: '500', fontSize: '13px', lineHeight: '1.3', marginBottom: '2px' }}>{option.activity_type}</div>
      {option.activity_type_code && (
        <div style={{ fontSize: '11px', color: 'var(--text-color-secondary)', lineHeight: '1.2' }}>{option.activity_type_code}</div>
      )}
    </div>
  );
};

const billingCodeValueTemplate = (option) => {
  if (option) {
    return (
      <div className="billing-code">
        <span>{option.activity_type}</span>
        {option.activity_type_code && (
          <span className="" style={{ color: 'var(--text-color-secondary)', marginLeft: '6px' }}>
            ({option.activity_type_code})
          </span>
        )}
      </div>
    );
  }
  return <span style={{ fontSize: '14px', color: 'var(--text-color-secondary)' }}>Select billing code</span>;
};

function TimerManager() {
  const {
    timerCaseOverlay,
    timerList,
    setTimerList,
    caseSearchValue,
    setCaseSearchValue,
    showTimerManagement,
    setShowTimerManagement,
    onChangeNotes,
    onChangeBillingCode,
    loadTimer,
    getTimerDisplay,
    getRunningTimersCount,
    getPausedTimersCount,
    getTotalActiveTimersCount,
    handleDeleteTimer,
    handleStartNewTimer,
    handleTimerButtonClick,
  } = useTimerManager();

  const { addToast } = useToast();
  const { activityTypes: billingCodes } = useActivityTypes({ category: 'manual', status: 'active' }, false, true);

  const runningTimers = useMemo(() => timerList?.filter((t) => t.start_time && !t.end_time) || [], [timerList]);
  const pausedTimers = useMemo(
    () => timerList?.filter((t) => t.start_time === '' && t.end_time && t.time_entry_status !== 'COMPLETED') || [],
    [timerList]
  );
  const allActiveTimers = useMemo(() => [...runningTimers, ...pausedTimers], [runningTimers, pausedTimers]);
  const primaryActiveTimer = allActiveTimers[0];
  const totalActiveCount = allActiveTimers.length;
  const overlayTooltip = useMemo(
    () =>
      timerList && timerList.length > 0
        ? `Open Timer Management (${(timerList || []).filter((t) => t.time_entry_status !== 'COMPLETED').length} active)`
        : 'Start New Timer',
    [timerList]
  );

  return (
    <div className="d-flex align-items-center mx-2 timer-manager-container">
      {primaryActiveTimer ? (
        <div className="timer-mini-btn cursor-pointer d-flex align-items-center" onClick={handleTimerButtonClick}>
          <span
            style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              backgroundColor: primaryActiveTimer.start_time && !primaryActiveTimer.end_time ? '#22c55e' : '#f59e0b',
              display: 'inline-block',
              marginRight: '0',
              flexShrink: 0,
            }}
          ></span>
          <span
            className="F-size13 black-700 text-nowrap"
            style={{ fontWeight: '600', lineHeight: '1', minWidth: '68px', position: 'relative', top: '0.3px' }}
          >
            {getTimerDisplay(primaryActiveTimer)}
          </span>
          {totalActiveCount > 1 && (
            <div
              style={{
                minWidth: '16px',
                height: '16px',
                borderRadius: '50%',
                backgroundColor: '#2655b9',
                color: '#fff',
                fontSize: '11px',
                fontWeight: 'bold',
                textAlign: 'center',
                lineHeight: '16px',
                marginLeft: '4px',
              }}
            >
              {totalActiveCount}
            </div>
          )}
        </div>
      ) : (
        <button className="p-button p-button-secondary pointer" type="button" onClick={handleTimerButtonClick}>
          <GlobalTooltip tooltip={overlayTooltip} showDelay={500}>
            <i className="icon-timer pointer fs-5" />
          </GlobalTooltip>
        </button>
      )}
      {/* Timer Overlay Component */}
      <TimerOverlay
        timerCaseOverlay={timerCaseOverlay}
        showTimerManagement={showTimerManagement}
        setShowTimerManagement={setShowTimerManagement}
        timerList={timerList}
        setTimerList={setTimerList}
        loadTimer={loadTimer}
        caseSearchValue={caseSearchValue}
        setCaseSearchValue={setCaseSearchValue}
        billingCodes={billingCodes}
        onChangeNotes={onChangeNotes}
        onChangeBillingCode={onChangeBillingCode}
        billingCodeOptionTemplate={billingCodeOptionTemplate}
        billingCodeValueTemplate={billingCodeValueTemplate}
        getTimerDisplay={getTimerDisplay}
        getRunningTimersCount={getRunningTimersCount}
        getPausedTimersCount={getPausedTimersCount}
        getTotalActiveTimersCount={getTotalActiveTimersCount}
        handleDeleteTimer={handleDeleteTimer}
        handleStartNewTimer={handleStartNewTimer}
        addToast={addToast}
      />
    </div>
  );
}

export default React.memo(TimerManager);
